import { trackAnonymousEvent } from "../../../utils/analytics/track-anonymous-event";
import { reportAfterPendingMarketingSitePageViewEvent } from "../../../utils/analytics/marketing-site-screen-view";
import { getMarketingLanguageFromPath, MARKETING_LANGUAGES, MARKETING_LOCALES } from "../../../marketing-content/marketingLocales";
import { interfaceLanguageHintParam } from "../../../i18n/interfaceLanguageHandoff";
import { JUNGLE_CLOUDFLARE_BASE_URL } from "../../../constants/GlobalTypesAndEnumsAndConstants";
import {
  CAMPAIGN_ATTRIBUTION_PARAMS,
  hasCampaignAttributionParams,
  isAcquisitionTouchVisit,
  isExcludedUtmSource,
  isMetaAdVisit,
} from "../../../utils/acquisition-campaign-params";

// The product lives on its own origin. When the landing page is served from the marketing domain the
// handoff is a full navigation to this origin; when it is served from the app itself (local dev, the
// /testing preview) the same paths resolve relative to the current origin.
export const JUNGLE_APP_ORIGIN = "https://app.jungleai.com";
// `jungleai.localhost` resolves to the dev server in Chromium browsers without any hosts-file change,
// so the marketing-domain behaviour can be exercised locally at http://jungleai.localhost:3000/.
const MARKETING_SITE_HOSTNAMES = new Set(["jungleai.com", "www.jungleai.com", "jungleai.localhost"]);

// Google Ads conversion the Framer site fired from its call-to-action buttons.
const GOOGLE_ADS_LANDING_CONVERSION_SEND_TO = "AW-11279974391/kmSTCJHGkM8YEPf_2oIq";

// The real marketing hostnames, without the local alias.
const PRODUCTION_MARKETING_SITE_HOSTNAMES = new Set(["jungleai.com", "www.jungleai.com"]);

// A Vercel preview deployment (….vercel.app) normally behaves like the app. Opening any URL on it with
// `?marketing-preview=1` makes that tab behave like jungleai.com instead (landing page at "/", absolute
// links into the app, attribution persistence), so the marketing site can be reviewed before DNS points at
// it. `?marketing-preview=0` turns it off again. It has no effect on any other host.
const MARKETING_PREVIEW_QUERY_PARAM = "marketing-preview";
const MARKETING_PREVIEW_SESSION_STORAGE_KEY = "marketing_site_preview";

function isMarketingPreviewEnabledInThisTab(): boolean {
  if (!window.location.hostname.endsWith(".vercel.app")) return false;
  try {
    const requestedPreviewState = new URLSearchParams(window.location.search).get(MARKETING_PREVIEW_QUERY_PARAM);
    if (requestedPreviewState === "1") sessionStorage.setItem(MARKETING_PREVIEW_SESSION_STORAGE_KEY, "1");
    if (requestedPreviewState === "0") sessionStorage.removeItem(MARKETING_PREVIEW_SESSION_STORAGE_KEY);
    return sessionStorage.getItem(MARKETING_PREVIEW_SESSION_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function isMarketingSiteOrigin(): boolean {
  // Without a window this is the build-time renderer (src/marketing-prerender.tsx), which only renders
  // the marketing site.
  if (typeof window === "undefined") return true;
  return MARKETING_SITE_HOSTNAMES.has(window.location.hostname) || isMarketingPreviewEnabledInThisTab();
}

/** True only on jungleai.com / www.jungleai.com (not the local alias, not a preview deployment). */
export function isProductionMarketingSiteHostname(hostname: string): boolean {
  return PRODUCTION_MARKETING_SITE_HOSTNAMES.has(hostname);
}

function readLocalStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocalStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage can be unavailable (private mode); attribution then only survives on the current URL.
  }
}

// Attribution parameters the app reads when a visitor arrives (the "set up tracking from different
// sources" effect in AppRouter), and the localStorage key each is remembered under on this origin.
// The marketing site and the app are different origins, so the only way these reach the app is on
// the URL of the handoff.
const FORWARDED_ATTRIBUTION_PARAMS: Array<{ param: string; localStorageKey: string }> = [
  { param: "r", localStorageKey: "ref_uid" }, // encrypted id of the Jungle user who referred this visitor
  { param: "ref", localStorageKey: "tolt_ref_id" }, // Tolt affiliate code
  { param: "a", localStorageKey: "a" }, // ambassador id
  { param: "gc_id", localStorageKey: "gc_id" }, // Google Ads campaign
  { param: "ttc_id", localStorageKey: "ttc_id" }, // TikTok campaign
  { param: "ttclid", localStorageKey: "ttclid" }, // TikTok click id
  { param: "rc_id", localStorageKey: "rc_id" }, // Reddit campaign
  { param: "ic_id", localStorageKey: "ic_id" }, // influencer campaign
  { param: "nlc_id", localStorageKey: "nlc_id" }, // newsletter campaign
  { param: "utm_source", localStorageKey: "utm_source" },
  { param: "utm_medium", localStorageKey: "utm_medium" },
  { param: "utm_campaign", localStorageKey: "utm_campaign" },
  { param: "utm_content", localStorageKey: "utm_content" },
  { param: "utm_term", localStorageKey: "utm_term" },
  { param: "utm_id", localStorageKey: "utm_id" }, // Meta: {{campaign.id}}
  { param: "fb_adset_id", localStorageKey: "fb_adset_id" }, // Meta: {{adset.id}}
  { param: "fb_ad_id", localStorageKey: "fb_ad_id" }, // Meta: {{ad.id}}
  { param: "fb_placement", localStorageKey: "fb_placement" }, // Meta: {{placement}}
  { param: "fb_site", localStorageKey: "fb_site" }, // Meta: {{site_source_name}}
  { param: "gclid", localStorageKey: "gclid" },
  { param: "fbclid", localStorageKey: "fbclid" },
  { param: "ws", localStorageKey: "web_session_id" },
];

// The device the referrer shared their link from (`s`: 46 iPhone, 20 browser, 19 Android) only means something
// with the referrer it came with, so it is remembered and forwarded together with `r`, never on its own.
const SHARER_DEVICE_PARAM = "s";
const SHARER_DEVICE_LOCAL_STORAGE_KEY = "ref_sharer_device";

// The utm_*, Meta ad, click and TikTok campaign params describe one campaign visit, so they are remembered and
// forwarded as one set (a later campaign replaces the whole set instead of mixing into it). Referral,
// ambassador, the other legacy campaign ids (gc_id, rc_id, …) and the web session keep per-key handling.
const CAMPAIGN_PARAM_NAMES = new Set(CAMPAIGN_ATTRIBUTION_PARAMS);
function isCampaignParam(param: string): boolean {
  return CAMPAIGN_PARAM_NAMES.has(param);
}

// A visit that replaces the remembered campaign: an acquisition touch, or a TikTok ad visit, whose URL can
// carry only its campaign id. Email and push notification links never replace an ad campaign.
function isNewCampaignVisit(params: URLSearchParams): boolean {
  if (isAcquisitionTouchVisit(params)) return true;
  return Boolean(params.get("ttc_id")) && !isExcludedUtmSource(params.get("utm_source"));
}

function removeLocalStorage(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Same as writeLocalStorage.
  }
}

/**
 * Remembers the attribution parameters on the current URL. A visitor usually lands with them on one page
 * (an ad click to the landing page or a blog post) and signs up from another, so without this the
 * campaign would be lost on the first internal navigation. Only on the marketing domain: on the app's own
 * origin AppRouter owns these keys and validates them (e.g. a referral id must not be the visitor's own).
 */
export function rememberAttributionParamsFromCurrentUrl(): void {
  if (!isMarketingSiteOrigin()) return;
  const currentUrlParams = new URLSearchParams(window.location.search);
  // A new campaign touch replaces the remembered campaign as a whole. Campaign params that are not a touch
  // (utm_source=email, push notifications) still forward from this page's URL but are not remembered over
  // an ad campaign.
  const isNewCampaignTouch = isNewCampaignVisit(currentUrlParams);
  for (const { param, localStorageKey } of FORWARDED_ATTRIBUTION_PARAMS) {
    const paramValue = currentUrlParams.get(param);
    if (isCampaignParam(param)) {
      if (!isNewCampaignTouch) continue;
      if (paramValue) writeLocalStorage(localStorageKey, paramValue);
      else removeLocalStorage(localStorageKey);
      continue;
    }
    if (paramValue) writeLocalStorage(localStorageKey, paramValue);
  }
  // A new referrer replaces the remembered one's device, even when its link carried none.
  if (currentUrlParams.get("r")) {
    const sharerDeviceOnUrl = currentUrlParams.get(SHARER_DEVICE_PARAM);
    if (sharerDeviceOnUrl) writeLocalStorage(SHARER_DEVICE_LOCAL_STORAGE_KEY, sharerDeviceOnUrl);
    else removeLocalStorage(SHARER_DEVICE_LOCAL_STORAGE_KEY);
  }
  // Legacy flag read by getForwardedTrackingParams (and on the app by AppRouter): sticky once set.
  if (isMetaAdVisit(currentUrlParams)) {
    writeLocalStorage("did_user_come_from_meta_ad", "true");
  }
  // No per-ad touch is recorded here: this origin's storage is not the app's, which records the forwarded
  // campaign as its touch (touch_source "marketing_site") when the handoff lands (App.tsx at startup).
}

/**
 * The attribution params forwarded into the app on every handoff: whatever is on the current URL, plus
 * values remembered from an earlier page of this visit (rememberAttributionParamsFromCurrentUrl). The
 * Framer site forwarded a subset of these (its `getQueryParameters`); the app reads all of them on
 * arrival (AppRouter) to attribute sign-ups, referrals and ad campaigns.
 */
export function getForwardedTrackingParams(): URLSearchParams {
  // Only the attribution allowlist crosses over, never the page's whole query: a stray `?redirect=1` on
  // a marketing URL would otherwise reach the app's /upgrade, whose own `redirect` handling sends the
  // visitor straight back.
  const currentUrlParams = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  // A campaign on this page's URL is forwarded on its own, never merged with a remembered earlier one.
  const urlCarriesCampaign = hasCampaignAttributionParams(currentUrlParams);
  const forwardedParams = new URLSearchParams();
  for (const { param, localStorageKey } of FORWARDED_ATTRIBUTION_PARAMS) {
    const valueOnUrl = currentUrlParams.get(param);
    if (valueOnUrl) {
      forwardedParams.set(param, valueOnUrl);
      continue;
    }
    if (urlCarriesCampaign && isCampaignParam(param)) continue;
    const rememberedValue = readLocalStorage(localStorageKey);
    if (rememberedValue && rememberedValue !== "null") {
      forwardedParams.set(param, rememberedValue);
    }
  }
  if (forwardedParams.has("r")) {
    // A referrer on this page's URL brings its own device (or none), never the one remembered for an earlier referrer.
    const sharerDeviceCode = currentUrlParams.get("r")
      ? currentUrlParams.get(SHARER_DEVICE_PARAM)
      : readLocalStorage(SHARER_DEVICE_LOCAL_STORAGE_KEY);
    if (sharerDeviceCode && sharerDeviceCode !== "null") forwardedParams.set(SHARER_DEVICE_PARAM, sharerDeviceCode);
  }

  // Legacy: the app recognises a Meta visitor by utm_source=facebook. Only added when no campaign is being
  // forwarded (a TikTok click counts), so it can never be attached to another campaign's parameters.
  const didUserComeFromMetaAd =
    readLocalStorage("did_user_come_from_meta_ad") === "true" || readLocalStorage("did_user_come_from_meta") === "true";
  const isForwardingACampaign = hasCampaignAttributionParams(forwardedParams);
  if (didUserComeFromMetaAd && !isForwardingACampaign) {
    forwardedParams.set("utm_source", "facebook");
  }
  return forwardedParams;
}

/**
 * A URL into the app at `appPath`, carrying the forwarded attribution params plus `extraParams`.
 * `withVisitorParams: false` leaves out everything read from this browser (the visit's attribution, the
 * web session id): what a link must say in the build-time rendering and in React's first render when it
 * adopts that rendering, since the two must match. `useAppUrl` in useAppUrl.ts does that switch.
 */
export function buildAppUrl(
  appPath: string,
  extraParams: Record<string, string> = {},
  { withVisitorParams = true, localeHint = undefined }: { withVisitorParams?: boolean; localeHint?: string } = {}
): string {
  const params = withVisitorParams ? getForwardedTrackingParams() : new URLSearchParams();
  for (const [paramName, paramValue] of Object.entries(extraParams)) {
    params.set(paramName, paramValue);
  }
  const pageLocale = localeHint ?? (withVisitorParams && typeof window !== "undefined"
    ? MARKETING_LOCALES[getMarketingLanguageFromPath(window.location.pathname)].productLocale : undefined);
  // URL language is authority for the marketing page. A query parameter cannot
  // forge that hint; it never becomes an explicit account preference in the app.
  if (MARKETING_LANGUAGES.some(language => MARKETING_LOCALES[language].productLocale === pageLocale)) params.set(interfaceLanguageHintParam, pageLocale);
  else params.delete(interfaceLanguageHintParam);
  const query = params.toString();
  const origin = isMarketingSiteOrigin() ? JUNGLE_APP_ORIGIN : "";
  return `${origin}${appPath}${query ? `?${query}` : ""}`;
}

/**
 * Hands the visitor to the app. From the marketing domain that is a full navigation to the app's
 * origin; when the landing page is served by the app itself, an in-app navigation is used instead
 * (no reload, and the Vite dev server would otherwise reject a `?url=` query as an asset import).
 */
export function goToApp(
  appPath: string,
  extraParams: Record<string, string> = {},
  navigateWithinApp?: (to: string) => void,
  // Values for the app only: a URL fragment never reaches a server, a Referer header or an analytics page URL.
  fragmentParams: Record<string, string> = {}
) {
  const fragment = new URLSearchParams(fragmentParams).toString();
  const appUrl = `${buildAppUrl(appPath, extraParams)}${fragment ? `#${fragment}` : ""}`;
  if (!isMarketingSiteOrigin() && navigateWithinApp) {
    navigateWithinApp(appUrl);
    return;
  }
  window.location.assign(appUrl);
}

/** Fires the Google Ads conversion the Framer call-to-action buttons reported. No-op without gtag. */
export function fireGoogleAdsLandingConversion() {
  const gtag = (window as any).gtag;
  if (typeof gtag !== "function") return;
  try {
    gtag("event", "conversion", { send_to: GOOGLE_ADS_LANDING_CONVERSION_SEND_TO, transaction_id: "" });
  } catch {
    // Ad blockers can stub gtag out; a failed conversion ping must never block the handoff.
  }
}

/**
 * Tracks a landing page event with the design the visitor was shown, so every step of the landing funnel
 * (views, clicks, uploads) can be split between the designs under test.
 */
export function trackLandingEvent(eventName: string, eventProperties: Record<string, unknown> = {}) {
  const landingEventProperties = { landingVariant: readLocalStorage(LANDING_VARIANT_LOCAL_STORAGE_KEY), ...eventProperties };
  // The landing view may still be waiting on the account lookup; an action must not be reported before it.
  reportAfterPendingMarketingSitePageViewEvent(() => trackAnonymousEvent(eventName, landingEventProperties));
}

export function trackLandingCallToActionClick(callToActionName: string, extraProperties: Record<string, unknown> = {}) {
  trackLandingEvent("Landing Page: Call To Action Clicked", { callToAction: callToActionName, ...extraProperties });
  // The Framer site only counted the Google Ads conversion when the visitor arrived from a Google
  // campaign (`gc_id`), except for the upload button, which always counted.
  const forwardedParams = getForwardedTrackingParams();
  const arrivedFromGoogleCampaign = Boolean(forwardedParams.get("gc_id"));
  if (arrivedFromGoogleCampaign || callToActionName === "upload_slides") {
    fireGoogleAdsLandingConversion();
  }
  // Visitors from a TikTok campaign (`ttc_id`) are reported to TikTok's events API through the worker.
  const tiktokCampaignId = forwardedParams.get("ttc_id");
  if (tiktokCampaignId) {
    reportTikTokLandingConversion(
      callToActionName === "paste_link" ? "CompleteRegistration" : "TryItNowButtonClick",
      tiktokCampaignId
    );
  }
}

// The event names are the ones the Framer site sent; the worker's /tt_tracking route maps them on.
function reportTikTokLandingConversion(
  eventName: "CompleteRegistration" | "TryItNowButtonClick",
  tiktokCampaignId: string
) {
  fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/tt_tracking`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      eventName,
      campaignId: tiktokCampaignId,
      tiktokClickId: getForwardedTrackingParams().get("ttclid") || readLocalStorage("ttclid"),
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    }),
    keepalive: true,
  }).catch(() => {});
}

// ── A/B variant ─────────────────────────────────────────────────────────────────────────────────
// The landing design every visitor gets. Until 2026-10-06 this was a 50/50 test of "moss" (dark) and
// "clearing" (light); the owner ended it with dark as the winner. The list and the per-visitor
// assignment stay so a future test only has to add a design here and in
// scripts/generate-marketing-static-html.mjs. A visitor remembered as "clearing" is reassigned.
export const LANDING_VARIANT_LOCAL_STORAGE_KEY = "landing_variant";
export const LANDING_AB_TEST_VARIANTS = ["moss"] as const;
export type LandingAbTestVariant = (typeof LANDING_AB_TEST_VARIANTS)[number];

// The generated marketing pages assign the variant in an inline script before the first paint and put it
// on <html> (scripts/generate-marketing-static-html.mjs); React must render that same variant to adopt the
// static HTML, storage or no storage.
const LANDING_VARIANT_HTML_ATTRIBUTE = "data-landing-variant";

function isLandingAbTestVariant(value: string | null | undefined): value is LandingAbTestVariant {
  return LANDING_AB_TEST_VARIANTS.includes(value as LandingAbTestVariant);
}

/** `?v=<design>` shows that design whatever the visitor was assigned (LandingPage reads the same parameter). */
export const LANDING_VERSION_QUERY_PARAM = "v";
function isLandingDesignForcedByUrl(): boolean {
  return new URLSearchParams(window.location.search).has(LANDING_VERSION_QUERY_PARAM);
}

export function resolveAssignedLandingVariant(): LandingAbTestVariant {
  const variantOnPage =
    typeof document !== "undefined" ? document.documentElement.getAttribute(LANDING_VARIANT_HTML_ATTRIBUTE) : null;
  if (isLandingAbTestVariant(variantOnPage)) return variantOnPage;
  const rememberedVariant = readLocalStorage(LANDING_VARIANT_LOCAL_STORAGE_KEY);
  if (isLandingAbTestVariant(rememberedVariant)) return rememberedVariant;
  const assignedVariant = LANDING_AB_TEST_VARIANTS[Math.floor(Math.random() * LANDING_AB_TEST_VARIANTS.length)];
  try {
    localStorage.setItem(LANDING_VARIANT_LOCAL_STORAGE_KEY, assignedVariant);
  } catch {
    // Storage can be unavailable (private mode); the visitor just gets re-rolled next visit.
  }
  return assignedVariant;
}

export function generateUuidV4(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const randomNibble = (Math.random() * 16) | 0;
    return (character === "x" ? randomNibble : (randomNibble & 0x3) | 0x8).toString(16);
  });
}

// ── Web session ─────────────────────────────────────────────────────────────────────────────────
// The marketing site and the app are different origins, so the landing page cannot see the app's
// auth state. Instead it keeps a "web session" id that the app marks as logged-in once the visitor
// signs in (AuthContext posts to update_web_session); a later visit to the landing page checks it
// and sends a signed-in visitor straight to the app. Ported from the Framer site's inline script.
const WEB_SESSION_LOCAL_STORAGE_KEY = "web_session_id";

let landingInteractionRevision = 0;

// A slow session lookup must never replace an upload or link the visitor has already started.
export function markLandingInteraction(): void {
  landingInteractionRevision += 1;
}

/**
 * What the landing page can know about a visitor's Jungle account. The experiment compares designs by
 * how many NEW visitors become users, so a visitor who already has an account must not be counted as one.
 * `signed_out` still means "has an account": signing out only clears the session's logged-in flag.
 */
export type LandingVisitorAccountState = "no_known_account" | "signed_in" | "signed_out";

/**
 * Looks up this browser's web session, or creates one for a first-time browser. A first-time browser has
 * nothing to look up, so it resolves without waiting on the network. A failed lookup resolves to
 * `no_known_account`: most visitors are new, and a real visitor must not vanish because a check failed.
 */
export async function checkLandingVisitorWebSession(): Promise<LandingVisitorAccountState> {
  if (typeof window === "undefined" || !isMarketingSiteOrigin()) return "no_known_account";
  const jsonHeaders = { "Content-Type": "application/json" };
  const existingWebSessionId = readLocalStorage(WEB_SESSION_LOCAL_STORAGE_KEY);
  if (existingWebSessionId) {
    try {
      const response = await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/users/verify_web_session`, {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify({ session_id: existingWebSessionId }),
      });
      const webSessionVerificationResponse = await response.json();
      if (webSessionVerificationResponse?.status !== "success") return "no_known_account";
      if (webSessionVerificationResponse.is_logged_in) return "signed_in";
      return webSessionVerificationResponse.has_linked_account ? "signed_out" : "no_known_account";
    } catch {
      return "no_known_account";
    }
  }
  const newWebSessionId = generateUuidV4();
  try {
    localStorage.setItem(WEB_SESSION_LOCAL_STORAGE_KEY, newWebSessionId);
  } catch {
    return "no_known_account";
  }
  fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/users/create_web_session`, {
    method: "POST",
    headers: jsonHeaders,
    // The design is recorded on the session: sessions that later get linked to a signed-in user are how
    // the designs are compared with each other and with the previous landing page. A design forced
    // through the URL (`?v=`, a preview link) is not the assigned one, so that session records none.
    body: JSON.stringify({
      session_id: newWebSessionId,
      landing_variant: isLandingDesignForcedByUrl() ? null : resolveAssignedLandingVariant(),
    }),
  }).catch(() => {});
  return "no_known_account";
}

// The page view waits for the account check so an account holder is never counted as a new visitor, but
// a slow check must not hold the view back indefinitely.
export const LANDING_VISITOR_ACCOUNT_CHECK_TIMEOUT_MS = 1500;

export function resolveLandingVisitorAccountStateForPageView(
  landingVisitorAccountCheck: Promise<LandingVisitorAccountState>
): Promise<LandingVisitorAccountState> {
  return new Promise((resolve) => {
    const timeoutId = setTimeout(() => resolve("no_known_account"), LANDING_VISITOR_ACCOUNT_CHECK_TIMEOUT_MS);
    void landingVisitorAccountCheck.then((landingVisitorAccountState) => {
      clearTimeout(timeoutId);
      resolve(landingVisitorAccountState);
    });
  });
}

export const LANDING_PAGE_VIEWED_EVENT_NAME = "Landing Page Viewed";
export const LANDING_PAGE_VIEWED_BY_EXISTING_ACCOUNT_EVENT_NAME = "Landing Page Viewed By Existing Account";

/**
 * Which page view event a landing visitor reports. "Landing Page Viewed" is the experiment's visitor count,
 * so only a browser with no known account may emit it; an account holder reports a separate event, which
 * keeps every existing count of the original event correct without a filter.
 */
export function buildLandingPageViewEvent({
  landingVisitorAccountState,
  pageViewEventProperties,
}: {
  landingVisitorAccountState: LandingVisitorAccountState;
  pageViewEventProperties: Record<string, unknown>;
}): { pageViewEventName: string; pageViewEventProperties: Record<string, unknown> } {
  if (landingVisitorAccountState === "no_known_account") {
    return { pageViewEventName: LANDING_PAGE_VIEWED_EVENT_NAME, pageViewEventProperties };
  }
  // appStoreRoute (phone visitors from a Meta ad offered the app) is registered on "Landing Page Viewed" only.
  const { appStoreRoute: _newVisitorOnlyAppStoreRoute, ...existingAccountEventProperties } = pageViewEventProperties;
  return {
    pageViewEventName: LANDING_PAGE_VIEWED_BY_EXISTING_ACCOUNT_EVENT_NAME,
    pageViewEventProperties: { ...existingAccountEventProperties, accountState: landingVisitorAccountState },
  };
}

/**
 * Sends a signed-in visitor straight to the app. Pass the page's already-started account check so the
 * redirect and the page view share one lookup; without it this starts its own.
 */
export async function redirectToAppIfAlreadyLoggedIn(
  signal?: AbortSignal,
  landingVisitorAccountCheck: Promise<LandingVisitorAccountState> = checkLandingVisitorWebSession()
): Promise<void> {
  const revisionAtStart = landingInteractionRevision;
  const landingVisitorAccountState = await landingVisitorAccountCheck;
  // The page view waits on this same lookup through a longer promise chain. Yield one task so it is
  // reported with the visitor's real account state before the navigation can cut it off.
  await new Promise((resolve) => setTimeout(resolve, 0));

  const redirectWasNotCancelled = !signal?.aborted;
  const visitorHasNotStartedUsingTheLandingPage = revisionAtStart === landingInteractionRevision;
  const visitorIsSignedIn = landingVisitorAccountState === "signed_in";
  if (redirectWasNotCancelled && visitorHasNotStartedUsingTheLandingPage && visitorIsSignedIn) {
    window.location.href = buildAppUrl("/");
  }
}
